/**
 * Test API Step Component
 *
 * Step 3: Test the API key with a live request.
 *
 * @module components/onboarding/TestApiStep
 */

import { Button, Alert } from '@/components/ui';
import { Play, MapPin, ArrowRight, RefreshCw } from 'lucide-react';
import type { GeoLookupResponse } from '@/hooks/useOnboarding';

// =============================================================================
// Types
// =============================================================================

export interface TestApiStepProps {
  /** Callback to test the API */
  onTest: () => Promise<void>;
  /** Whether the test is running */
  loading: boolean;
  /** Test result if available */
  result: GeoLookupResponse | null;
  /** Error message if any */
  error: string | null;
  /** Callback when user continues */
  onContinue: () => void;
}

// =============================================================================
// Component
// =============================================================================

export function TestApiStep({
  onTest,
  loading,
  result,
  error,
  onContinue,
}: TestApiStepProps) {
  const isSuccess = result?.success && result.data;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-display font-semibold text-gray-900">
          🔍 Test your API key
        </h2>
        <p className="text-gray-500 mt-2">
          Let's make sure everything is working properly.
        </p>
      </div>

      {/* Test button or result */}
      {!isSuccess ? (
        <div className="space-y-4">
          <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 text-center">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-brand-100 rounded-full">
                <Play className="h-6 w-6 text-brand-600" />
              </div>
            </div>
            <p className="text-gray-600 mb-4">
              We'll call the Geolocation API to look up your current location.
            </p>
            <Button
              onClick={onTest}
              loading={loading}
              leftIcon={loading ? undefined : <Play className="h-4 w-4" />}
            >
              {loading ? 'Testing...' : 'Test My API Key'}
            </Button>
          </div>

          {error && (
            <Alert variant="error">
              <div className="flex items-center justify-between gap-4 w-full">
                <p>{error}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onTest}
                  leftIcon={<RefreshCw className="h-3 w-3" />}
                >
                  Retry
                </Button>
              </div>
            </Alert>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Success message */}
          <Alert variant="success">
            <span className="font-medium">API call successful!</span>
          </Alert>

          {/* Location result */}
          <div className="p-6 bg-brand-50 rounded-lg border border-brand-200">
            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-brand-100 rounded-full flex-shrink-0">
                <MapPin className="h-6 w-6 text-brand-600" />
              </div>
              <div>
                <p className="text-lg font-medium text-gray-900">
                  You're in {result.data?.location?.city_name || 'Unknown City'},{' '}
                  {result.data?.location?.region_name || 'Unknown State'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  IP: {result.data?.ip} | {result.data?.location?.country_name || 'Unknown Country'}
                </p>
              </div>
            </div>
          </div>

          {/* Raw response */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">API Response</p>
            <pre className="p-4 bg-gray-900 rounded-lg overflow-x-auto text-sm">
              <code className="text-green-400">
                {JSON.stringify(result, null, 2)}
              </code>
            </pre>
          </div>

          <Button
            onClick={onContinue}
            rightIcon={<ArrowRight className="h-4 w-4" />}
          >
            Continue
          </Button>
        </div>
      )}
    </div>
  );
}
